import React, { useState, useEffect, useRef, useCallback } from 'react'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import {
  GlobalDevStyles, PageWrap, Topbar, Body,
  MainCol, ScanCard, ScanInput, HintRow,
  ListPanel, RastreiosList, RastreioRow,
  BulkActionBtn, SmallBtn,
  SideCol, SideHeader, SummaryStats, SummaryStat,
  OccList, OccEmpty, OccItem,
  ConfirmOverlay, ConfirmBox, ConfirmBtn,
} from './style'
import FeedbackModal from '../_shared/FeedbackModal'
import { printReturnRomaneio, printNaoEmbarcado } from '../_shared/printRomaneio'
import { toast } from 'react-toastify'
import { useAudioUnlock, playErrorBeep, playWarningBeep, playSuccessBeep } from '../_shared/sounds'
import { useScannerInput, useIsCoarsePointer, HIDDEN_INPUT_STYLE } from '../_shared/scanner'
import { normalizeRastreio } from '../_shared/rastreio'

// Data de hoje no horário LOCAL (sem toISOString, que converteria pra UTC).
function todayLocalISO() {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * Tela DEVOLUÇÃO
 *
 * UX: operador bipa N rastreios (acumula numa lista) e dispara o batch
 *     com POST /api/santolog/devolucao { rastreios: [...] }.
 * Backend marca os pedidos correspondentes como nao_embarcado (cascata pros
 * volumes) e devolve summary + ocorrências de rastreios que não foram aceitos.
 *
 * reasons em ocorrencias:
 *  - not_found                       → rastreio inválido / não cadastrado
 *  - volume_embarcado                → volume já embarcou
 *  - order_has_embarcado_volume      → outro volume do pedido já está embarcado
 */
export default function SantoLogDevolucao(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  // Estado do batch
  const [scanValue, setScanValue] = useState('')
  const [rastreios, setRastreios] = useState([])    // array de rastreios bipados (deduplicados)
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState({ kind: null, message: '' })
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Resultado da última devolução
  const [lastResult, setLastResult] = useState(null)
  // lastResult = { summary: { scanned, affected_orders, ignored }, ocorrencias: [...] }

  // Mapa dos rastreios bipados que estão com divergência (pedido bloqueado).
  // Chave é o rastreio em lowercase; valor tem order_number, reason, missing_count.
  // Usado pra destacar visualmente esses rastreios na lista de coletados.
  const [problemRastreios, setProblemRastreios] = useState({})

  // Modo da tela: 'pedido' (bipa rastreios em batch) | 'box' (bipa 1 box → devolve tudo)
  const [mode, setMode] = useState('pedido')
  const [boxScanValue, setBoxScanValue] = useState('')
  const [pendingBox, setPendingBox] = useState(null) // box resolvido aguardando confirmação
  const [naoEmbDate, setNaoEmbDate] = useState(todayLocalISO())

  const scanInputRef = useRef(null)
  const boxInputRef = useRef(null)

  // Destrava AudioContext compartilhado + modal de feedback
  useAudioUnlock()
  const initialModal = { open: false, kind: 'error', title: '', message: '' }
  const [modal, setModal] = useState(initialModal)
  const modalOpenRef = useRef(false)
  useEffect(() => { modalOpenRef.current = modal.open }, [modal.open])

  // Scanner: arma o input sem abrir teclado virtual
  const isCoarse = useIsCoarsePointer()
  const { arm: armScanner } = useScannerInput({
    getInput: () => (mode === 'box' ? boxInputRef.current : scanInputRef.current),
    getDisabled: () => auth !== 'authorized' || modal.open || confirmOpen || !!pendingBox,
    armKey: mode,
  })

  useEffect(() => { verifyAuth() }, []) // eslint-disable-line

  // Foco perpétuo no input
  useEffect(() => {
    if (auth !== 'authorized') return
    if (confirmOpen || modal.open || pendingBox) return
    const t = setTimeout(armScanner, 120)
    return () => clearTimeout(t)
  }, [auth, confirmOpen, modal.open, busy, pendingBox, mode, armScanner])

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (confirmOpen || modalOpenRef.current) return
      const ae = document.activeElement
      if (ae && ae !== document.body) {
        const tag = ae.tagName
        if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      }
      armScanner()
    }, 50)
  }, [confirmOpen, armScanner])

  // ── ADD / REMOVE rastreio na lista ────────────────────────────────────────
  function addRastreio(code) {
    // Normaliza qualquer prefixo (SLEXPRESSO, TMRAPIDO, etc) para ALEF{NF}V{VOL}
    const r = normalizeRastreio(code) || ''
    if (!r) return
    setRastreios(prev => {
      // Dedup case-insensitive
      const exists = prev.find(x => x.toLowerCase() === r.toLowerCase())
      if (exists) {
        playWarningBeep()
        setFeedback({ kind: null, message: `⚠ ${r} já está na lista` })
        return prev
      }
      playSuccessBeep()
      setFeedback({ kind: 'ok', message: `+ ${r}` })
      return [...prev, r]
    })
  }

  function removeRastreio(code) {
    setRastreios(prev => prev.filter(x => x !== code))
    // Remove também do índice de divergências (se o rastreio saiu da lista,
    // faz sentido limpar a marcação — ele deixa de contar)
    setProblemRastreios(prev => {
      const key = String(code || '').toLowerCase()
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  function clearAll() {
    setRastreios([])
    setProblemRastreios({})
    setFeedback({ kind: null, message: '' })
    setLastResult(null)
  }

  function onScanKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      const code = scanValue.trim()
      setScanValue('')
      if (code) addRastreio(code)
    }
    if (e.key === 'Escape') setScanValue('')
  }

  // ── ENVIO DO BATCH ────────────────────────────────────────────────────────
  function openConfirm() {
    if (rastreios.length === 0) return
    setConfirmOpen(true)
  }

  // ── APLICA O RESULTADO (compartilhado entre modo pedido e modo box) ─────────
  function applyDevolucaoResult(data, { fromBox = false } = {}) {
    const blockedOrders = Array.isArray(data.blocked_orders) ? data.blocked_orders : []
    const summary = data.summary || {
      scanned: fromBox ? 0 : rastreios.length,
      affected_orders: 0,
      blocked_orders: blockedOrders.length,
      ignored: (data.ocorrencias || []).length,
    }
    const occCount = (data.ocorrencias || []).length
    const blockedCount = blockedOrders.length

    setLastResult({
      summary,
      affected_order_ids: data.affected_order_ids || [],
      blocked_orders: blockedOrders,
      ocorrencias: data.ocorrencias || [],
      romaneio: data.romaneio || null,
      box: data.box || null,
    })

    // Índice de rastreios com divergência / trimming da lista: só no modo pedido.
    if (!fromBox) {
      const problemMap = {}
      blockedOrders.forEach((bo) => {
        const missingCount = (bo.missing_volumes || []).length
        ;(bo.scanned_volumes || []).forEach((sv) => {
          const key = String(sv.rastreio || '').toLowerCase()
          if (!key) return
          problemMap[key] = {
            order_number: bo.order_number,
            reason: bo.reason,
            missing_count: missingCount,
            missing_volumes: bo.missing_volumes || [],
          }
        })
      })
      setProblemRastreios(problemMap)
      // Mantém só os rastreios de pedidos bloqueados; os devolvidos somem.
      if (blockedCount > 0) {
        setRastreios((prev) => prev.filter((r) => problemMap[String(r).toLowerCase()]))
      } else {
        setRastreios([])
        setProblemRastreios({})
      }
    }

    if (blockedCount > 0) {
      playWarningBeep()
      setModal({
        open: true, kind: 'warning',
        title: `${blockedCount} pedido${blockedCount !== 1 ? 's' : ''} bloqueado${blockedCount !== 1 ? 's' : ''}`,
        message: fromBox
          ? `${summary.affected_orders} pedido${summary.affected_orders !== 1 ? 's' : ''} devolvido${summary.affected_orders !== 1 ? 's' : ''}. ${blockedCount} não pôde ser devolvido — pedido parcial (volume em outro box) ou volume já embarcado. Veja os detalhes ao lado.`
          : (summary.affected_orders > 0
            ? `${summary.affected_orders} pedido${summary.affected_orders !== 1 ? 's' : ''} devolvido${summary.affected_orders !== 1 ? 's' : ''}. ${blockedCount} pedido${blockedCount !== 1 ? 's' : ''} precisam ter mais volumes bipados — os rastreios com divergência foram MANTIDOS na lista pra você bipar os que faltam ou removê-los.`
            : `Nenhum pedido foi devolvido: ${blockedCount} pedido${blockedCount !== 1 ? 's têm' : ' tem'} volumes faltando. Os rastreios com divergência foram MANTIDOS na lista pra você bipar os que faltam ou removê-los.`),
      })
    } else if (occCount === 0) {
      playSuccessBeep()
      setModal({
        open: true, kind: 'success',
        title: 'Devolução concluída',
        message: `${summary.affected_orders} pedido${summary.affected_orders !== 1 ? 's' : ''} marcado${summary.affected_orders !== 1 ? 's' : ''} como Não Embarcado.`,
      })
    } else {
      playWarningBeep()
      setModal({
        open: true, kind: 'warning',
        title: 'Devolução com ocorrências',
        message: `${summary.affected_orders} pedido${summary.affected_orders !== 1 ? 's' : ''} devolvido${summary.affected_orders !== 1 ? 's' : ''}. ${occCount} ${occCount !== 1 ? 'ocorrências' : 'ocorrência'} — veja ao lado.`,
      })
    }
    setFeedback({
      kind: 'ok',
      message: `✓ ${summary.affected_orders} devolvido${summary.affected_orders !== 1 ? 's' : ''}${blockedCount > 0 ? ` · ${blockedCount} bloqueado${blockedCount !== 1 ? 's' : ''}` : ''}`,
    })
  }

  async function submitDevolucao() {
    setConfirmOpen(false)
    setBusy(true)
    setFeedback({ kind: null, message: 'Enviando devolução…' })
    try {
      const res = await apiSantoLog.post('/devolucao', { rastreios })
      applyDevolucaoResult(res.data || {}, { fromBox: false })
    } catch (err) {
      console.error('POST /devolucao failed:', err)
      const data = err?.response?.data || {}
      const msg = data.error || data.message || err.message
      playErrorBeep()
      setModal({ open: true, kind: 'error', title: 'Erro na devolução', message: 'Erro: ' + msg })
      setFeedback({ kind: 'err', message: 'Erro: ' + msg })
    } finally {
      setBusy(false)
    }
  }

  // ── MODO BOX: bipa box → resolve → confirma → POST /devolucao/box ────────────
  async function resolveBoxScan(code) {
    const barcode = String(code || '').trim()
    if (!barcode) return
    setBusy(true)
    setFeedback({ kind: null, message: 'Buscando box…' })
    try {
      const boxRes = await apiSantoLog.get(`/box/by-barcode/${encodeURIComponent(barcode)}`, { params: { module: 'devolucao' } }).catch((err) => {
        if (err?.response?.status === 404) return null
        throw err
      })
      if (!boxRes) {
        playErrorBeep()
        setModal({ open: true, kind: 'error', title: 'Box não encontrado', message: `Nenhum box com código "${barcode}" foi localizado.` })
        setFeedback({ kind: 'err', message: `Box "${barcode}" não encontrado` })
        return
      }
      const box = boxRes.data?.box || boxRes.data || {}
      playSuccessBeep()
      setFeedback({ kind: 'ok', message: `📦 ${box.name || barcode} — confirme a devolução` })
      setPendingBox({ id: box.id, name: box.name || barcode, barcode: box.barcode || barcode })
    } catch (err) {
      console.error('GET /box/by-barcode failed:', err)
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message
      playErrorBeep()
      setModal({ open: true, kind: 'error', title: 'Erro', message: 'Erro: ' + msg })
      setFeedback({ kind: 'err', message: 'Erro: ' + msg })
    } finally {
      setBusy(false)
    }
  }

  async function submitBoxDevolucao() {
    const box = pendingBox
    if (!box) return
    setPendingBox(null)
    setBusy(true)
    setFeedback({ kind: null, message: 'Devolvendo box…' })
    try {
      const res = await apiSantoLog.post('/devolucao/box', { box_id: box.id })
      applyDevolucaoResult(res.data || {}, { fromBox: true })
    } catch (err) {
      console.error('POST /devolucao/box failed:', err)
      const data = err?.response?.data || {}
      const msg = data.error || data.message || err.message
      playErrorBeep()
      setModal({ open: true, kind: 'error', title: 'Erro na devolução', message: 'Erro: ' + msg })
      setFeedback({ kind: 'err', message: 'Erro: ' + msg })
    } finally {
      setBusy(false)
      setBoxScanValue('')
    }
  }

  function onBoxScanKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      const code = boxScanValue.trim()
      setBoxScanValue('')
      if (code) resolveBoxScan(code)
    }
    if (e.key === 'Escape') setBoxScanValue('')
  }

  // ── IMPRESSÃO ───────────────────────────────────────────────────────────────
  function handlePrintRomaneio() {
    const romaneio = lastResult?.romaneio
    if (!romaneio || !(romaneio.items || []).length) {
      toast.warn('Sem romaneio para imprimir.')
      return
    }
    const { ok, error } = printReturnRomaneio(romaneio, { title: 'Romaneio de Devolução' })
    if (!ok && error === 'popup_blocked') toast.warn('Permita pop-ups neste site para imprimir.')
  }

  async function handlePrintNaoEmbarcado() {
    try {
      const { ok, error } = await printNaoEmbarcado({ date: naoEmbDate })
      if (!ok && error === 'popup_blocked') toast.warn('Permita pop-ups neste site para imprimir.')
    } catch (err) {
      toast.error('Erro ao gerar romaneio: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    }
  }

  function closeModal() {
    setModal(initialModal)
    setTimeout(armScanner, 30)
  }

  // ── DERIVED ───────────────────────────────────────────────────────────────
  const summary = lastResult?.summary
  const occCount = lastResult?.ocorrencias?.length || 0
  const blockedList = lastResult?.blocked_orders || []
  const blockedCount = summary?.blocked_orders ?? blockedList.length
  const okCount = summary ? (summary.scanned - (summary.ignored || 0)) : 0

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Devolução">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalDevStyles />
          <PageWrap>
            <Topbar>
              <h2>↩ Devolução</h2>
              <span className="crumb">Bipe rastreios (por pedido) ou um box inteiro — pedidos viram <strong style={{ color: '#ef4444' }}>Não Embarcado</strong></span>
              <span style={{ flex: 1 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="date"
                  value={naoEmbDate}
                  onChange={(e) => setNaoEmbDate(e.target.value)}
                  style={{ background: '#111318', color: '#e8ecf0', border: '1px solid #2a3040', borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}
                />
                <button
                  type="button"
                  onClick={handlePrintNaoEmbarcado}
                  style={{ background: '#1e2330', color: '#e8ecf0', border: '1px solid #2a3040', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", whiteSpace: 'nowrap' }}
                >
                  🖨 Não-embarcados do dia
                </button>
              </div>
            </Topbar>

            <Body>
              <MainCol>
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  {['pedido', 'box'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMode(m)}
                      disabled={busy}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                        fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
                        border: mode === m ? '1px solid #00e5a0' : '1px solid #2a3040',
                        background: mode === m ? 'rgba(0,229,160,.12)' : '#111318',
                        color: mode === m ? '#00e5a0' : '#8a95a3',
                      }}
                    >
                      {m === 'pedido' ? '📦 Por pedido' : '🗄 Por box'}
                    </button>
                  ))}
                </div>

                {mode === 'pedido' && (
                <>
                <ScanCard>
                  <h3>Bipar rastreios da devolução</h3>
                  <p>Bipe um rastreio de cada vez. Aceita prefixos <strong>ALEF</strong>, <strong>TMPASC</strong>, <strong>TMNEXT</strong>, <strong>EE</strong>. Duplicados são ignorados automaticamente.</p>
                  <ScanInput
                    ref={scanInputRef}
                    value={scanValue}
                    $kind={feedback.kind}
                    onChange={e => setScanValue(e.target.value)}
                    onKeyDown={onScanKeyDown}
                    onBlur={handleBlur}
                    placeholder="Bipe o rastreio"
                    disabled={busy || confirmOpen}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    style={isCoarse ? HIDDEN_INPUT_STYLE : undefined}
                  />
                  <HintRow>
                    <span>{feedback.message || 'Aguardando bipe…'}</span>
                    <span style={{ flex: 1 }} />
                    <span>Total: <span className="count">{rastreios.length}</span></span>
                  </HintRow>
                </ScanCard>

                <ListPanel>
                  <div className="header">
                    <h4>Rastreios coletados ({rastreios.length})</h4>
                    <div className="actions">
                      {rastreios.length > 0 && (
                        <SmallBtn onClick={clearAll} disabled={busy}>
                          ✕ Limpar tudo
                        </SmallBtn>
                      )}
                    </div>
                  </div>
                  {rastreios.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#5a6370', textAlign: 'center', padding: 20 }}>
                      Nenhum rastreio bipado ainda.<br />
                      Comece bipando o primeiro rastreio acima.
                    </div>
                  ) : (
                    <RastreiosList>
                      {rastreios.map((r, i) => {
                        const problem = problemRastreios[String(r).toLowerCase()];
                        return (
                          <RastreioRow
                            key={r}
                            style={problem ? {
                              background: 'rgba(239,68,68,.10)',
                              borderLeft: '3px solid #ef4444',
                              padding: '6px 10px',
                              borderRadius: 4,
                            } : undefined}
                            title={problem
                              ? `⚠ NF ${problem.order_number}: ${problem.missing_count} volume(s) faltando bipar`
                              : undefined}
                          >
                            <span className="idx">{i + 1}</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                              <span className="rastreio" style={problem ? { color: '#fca5a5' } : undefined}>
                                {r}
                              </span>
                              {problem && (
                                <span style={{
                                  fontSize: 10,
                                  color: '#f87171',
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 600,
                                }}>
                                  ⚠ NF {problem.order_number} · falta{problem.missing_count !== 1 ? 'm' : ''} {problem.missing_count} volume{problem.missing_count !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="remove"
                              onClick={() => removeRastreio(r)}
                              disabled={busy}
                            >
                              ✕
                            </button>
                          </RastreioRow>
                        );
                      })}
                    </RastreiosList>
                  )}
                </ListPanel>

                {rastreios.length > 0 && (
                  <BulkActionBtn onClick={openConfirm} disabled={busy}>
                    {busy ? 'Processando…' : `↩ Devolver ${rastreios.length} rastreio${rastreios.length !== 1 ? 's' : ''}`}
                  </BulkActionBtn>
                )}
                </>
                )}

                {mode === 'box' && (
                  <ScanCard>
                    <h3>Devolver box inteiro</h3>
                    <p>Bipe o código do <strong>box</strong>. Todos os volumes físicos nele serão devolvidos. Pedidos com volume em outro box são <strong>bloqueados</strong> (não devolve parcial).</p>
                    <ScanInput
                      ref={boxInputRef}
                      value={boxScanValue}
                      $kind={feedback.kind}
                      onChange={(e) => setBoxScanValue(e.target.value)}
                      onKeyDown={onBoxScanKeyDown}
                      onBlur={handleBlur}
                      placeholder="Bipe o código do box"
                      disabled={busy || !!pendingBox}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      style={isCoarse ? HIDDEN_INPUT_STYLE : undefined}
                    />
                    <HintRow>
                      <span>{feedback.message || 'Aguardando bipe do box…'}</span>
                    </HintRow>
                  </ScanCard>
                )}
              </MainCol>

              {/* SIDE COL — resumo + ocorrências da última devolução */}
              <SideCol>
                <SideHeader>
                  <h3>Última devolução</h3>
                  <p>{summary ? 'Resumo do último envio' : 'Nenhuma devolução nesta sessão'}</p>
                </SideHeader>
                <SummaryStats>
                  <SummaryStat $variant="ok">
                    <div className="l">Bipados</div>
                    <div className="v">{summary?.scanned ?? '—'}</div>
                  </SummaryStat>
                  <SummaryStat $variant="ok">
                    <div className="l">Devolvidos</div>
                    <div className="v">{summary?.affected_orders ?? '—'}</div>
                  </SummaryStat>
                  <SummaryStat $variant="warn">
                    <div className="l">Bloqueados</div>
                    <div className="v">{blockedCount || '—'}</div>
                  </SummaryStat>
                  <SummaryStat $variant="warn">
                    <div className="l">Ignorados</div>
                    <div className="v">{summary?.ignored ?? '—'}</div>
                  </SummaryStat>
                </SummaryStats>

                {lastResult?.romaneio && (lastResult.romaneio.items || []).length > 0 && (
                  <button
                    type="button"
                    onClick={handlePrintRomaneio}
                    style={{
                      marginTop: 12, width: '100%', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
                      border: '1px solid #00e5a0', background: 'rgba(0,229,160,.12)', color: '#00e5a0',
                    }}
                  >
                    🖨 Imprimir romaneio ({(lastResult.romaneio.items || []).length} pedido{(lastResult.romaneio.items || []).length !== 1 ? 's' : ''})
                  </button>
                )}

                {/* Painel de pedidos BLOQUEADOS: mostra os rastreios que faltam
                    bipar pra cada pedido finalizar. Operador lê e bipa os
                    que estão listados em "missing_volumes". */}
                {blockedList.length > 0 && (
                  <div style={{
                    marginTop: 12,
                    padding: '12px 14px',
                    background: 'rgba(239,68,68,.08)',
                    border: '1px solid rgba(239,68,68,.4)',
                    borderRadius: 8,
                  }}>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#f87171',
                      textTransform: 'uppercase',
                      letterSpacing: '.06em',
                      marginBottom: 10,
                    }}>
                      ⚠ Pedidos bloqueados ({blockedList.length}) — bipe os volumes faltantes
                    </div>
                    {blockedList.map((bo, idx) => {
                      const missing = Array.isArray(bo.missing_volumes) ? bo.missing_volumes : [];
                      const blocking = Array.isArray(bo.blocking_volumes) ? bo.blocking_volumes : [];
                      return (
                        <div
                          key={bo.order_id || idx}
                          style={{
                            padding: '10px 12px',
                            background: 'rgba(0,0,0,.25)',
                            border: '1px solid rgba(239,68,68,.25)',
                            borderRadius: 6,
                            marginBottom: idx < blockedList.length - 1 ? 8 : 0,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, fontWeight: 700, color: '#e8ecf0' }}>
                              NF {bo.order_number || '—'}
                            </span>
                            <span style={{
                              fontSize: 9,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              padding: '2px 6px',
                              borderRadius: 4,
                              background: bo.reason === 'order_has_terminal_volume'
                                ? 'rgba(239,68,68,.25)'
                                : 'rgba(245,158,11,.25)',
                              color: bo.reason === 'order_has_terminal_volume' ? '#fca5a5' : '#fbbf24',
                            }}>
                              {bo.reason === 'order_has_terminal_volume' ? 'volume terminal' : 'incompleto'}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.4, marginBottom: 8 }}>
                            {bo.message || 'Pedido não finalizado — bipe os volumes faltantes.'}
                          </div>

                          {missing.length > 0 && (
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#8a95a3', marginBottom: 4, textTransform: 'uppercase' }}>
                                Faltam bipar ({missing.length})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {missing.map((v) => (
                                  <div
                                    key={v.id || v.rastreio}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '5px 8px',
                                      background: 'rgba(245,158,11,.10)',
                                      border: '1px dashed rgba(245,158,11,.35)',
                                      borderRadius: 4,
                                      fontSize: 11,
                                      fontFamily: "'Space Mono', monospace",
                                    }}
                                  >
                                    <span style={{ color: '#fbbf24', fontWeight: 700 }}>
                                      {v.rastreio || '—'}
                                    </span>
                                    <span style={{ color: '#8a95a3', fontSize: 10 }}>
                                      V{v.volume ?? '?'} · {v.status || '—'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {blocking.length > 0 && (
                            <div style={{ marginTop: missing.length > 0 ? 8 : 0 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#f87171', marginBottom: 4, textTransform: 'uppercase' }}>
                                Volumes em status terminal ({blocking.length})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {blocking.map((v) => (
                                  <div
                                    key={v.id || v.rastreio}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '5px 8px',
                                      background: 'rgba(239,68,68,.12)',
                                      border: '1px solid rgba(239,68,68,.3)',
                                      borderRadius: 4,
                                      fontSize: 11,
                                      fontFamily: "'Space Mono', monospace",
                                    }}
                                  >
                                    <span style={{ color: '#fca5a5', fontWeight: 700 }}>
                                      {v.rastreio || '—'}
                                    </span>
                                    <span style={{ color: '#8a95a3', fontSize: 10 }}>
                                      V{v.volume ?? '?'} · {v.status || '—'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <OccList>
                  <h4>Ocorrências{occCount > 0 ? ` (${occCount})` : ''}</h4>
                  {!summary ? (
                    <OccEmpty>
                      Envie uma devolução pra ver as ocorrências aqui.
                    </OccEmpty>
                  ) : occCount === 0 && blockedList.length === 0 ? (
                    <OccEmpty>
                      ✓ Nenhuma ocorrência. {okCount} rastreio{okCount !== 1 ? 's' : ''} processado{okCount !== 1 ? 's' : ''} sem problemas.
                    </OccEmpty>
                  ) : (
                    lastResult.ocorrencias.map((occ, i) => (
                      <OccItem key={i} $reason={occ.reason}>
                        <div className="rastreio">{occ.rastreio || '—'}</div>
                        <div className="msg">{occ.message || occ.reason}</div>
                        <span className="tag">{occ.reason}</span>
                        {occ.current_status && (
                          <span style={{ fontSize: 9, color: '#5a6370', marginLeft: 6 }}>
                            status atual: {occ.current_status}
                          </span>
                        )}
                        {occ.missing_count > 0 && (
                          <span style={{ fontSize: 9, color: '#fbbf24', marginLeft: 6 }}>
                            · {occ.missing_count} faltando
                          </span>
                        )}
                      </OccItem>
                    ))
                  )}
                </OccList>
              </SideCol>
            </Body>
          </PageWrap>

          {/* MODAL — confirmação */}
          <ConfirmOverlay $visible={confirmOpen}>
            <ConfirmBox>
              <div className="icon">↩</div>
              <h3>Confirmar devolução de {rastreios.length} rastreio{rastreios.length !== 1 ? 's' : ''}?</h3>
              <p>
                Os pedidos correspondentes serão marcados como <strong style={{ color: '#fca5a5' }}>Não Embarcado</strong>.
                Esta ação afeta TODOS os volumes dos pedidos atingidos.
              </p>
              <div className="actions">
                <ConfirmBtn onClick={() => setConfirmOpen(false)} disabled={busy}>
                  Cancelar
                </ConfirmBtn>
                <ConfirmBtn $danger onClick={submitDevolucao} disabled={busy}>
                  {busy ? 'Enviando…' : 'Sim, devolver'}
                </ConfirmBtn>
              </div>
            </ConfirmBox>
          </ConfirmOverlay>

          {/* MODAL — confirmação da devolução por BOX */}
          <ConfirmOverlay $visible={!!pendingBox}>
            <ConfirmBox>
              <div className="icon">🗄</div>
              <h3>Devolver o box {pendingBox?.name}?</h3>
              <p>
                TODOS os volumes físicos no box <strong>{pendingBox?.name}</strong> serão devolvidos — os
                pedidos viram <strong style={{ color: '#fca5a5' }}>Não Embarcado</strong>. Pedidos com volume
                em outro box são bloqueados (sem devolução parcial).
              </p>
              <div className="actions">
                <ConfirmBtn onClick={() => { setPendingBox(null); setTimeout(armScanner, 30) }} disabled={busy}>
                  Cancelar
                </ConfirmBtn>
                <ConfirmBtn $danger onClick={submitBoxDevolucao} disabled={busy}>
                  {busy ? 'Devolvendo…' : 'Sim, devolver box'}
                </ConfirmBtn>
              </div>
            </ConfirmBox>
          </ConfirmOverlay>

          {/* MODAL — feedback genérico (success / warning / error) */}
          <FeedbackModal
            visible={modal.open}
            kind={modal.kind}
            title={modal.title}
            message={modal.message}
            onClose={closeModal}
          />
        </>
      )}
    </Dashboard>
  )
}
